// Top-right account widget on the public map: shows who's signed in, or a
// sign-in button with a read-only hint. Sign-in is the same Google flow as
// the admin panel (the account must be linked from the mobile app first).
//
// supabase-js is loaded lazily: on a clean anonymous visit the map bundle
// never pays for it — the auth module loads only when a stored session or
// an OAuth redirect is present, or the visitor clicks sign in.
import { api, ApiError } from '../api/client';
import {
  accountLabel,
  applyAccountProbe,
  shouldDropSession,
  shouldInitAuth,
  showAdminLink,
  type AccountProbe,
  type AccountState,
} from './account-state';

type AuthModule = typeof import('../auth/supabase');

export function mountAccountWidget(
  el: HTMLElement,
  onChange?: (state: AccountState) => void,
): void {
  let state: AccountState = { kind: 'signed_out' };
  let auth: AuthModule | undefined;

  const loadAuth = async (): Promise<AuthModule> => {
    auth ??= await import('../auth/supabase');
    return auth;
  };

  function setState(next: AccountState): void {
    state = next;
    onChange?.(next);
    render();
  }

  function render(): void {
    switch (state.kind) {
      case 'signed_out':
        el.innerHTML = `
          <button id="account-sign-in" type="button">Sign in</button>
          <span class="account-hint">read-only until signed in</span>`;
        el.querySelector('#account-sign-in')?.addEventListener('click', () => {
          void loadAuth()
            .then((a) => a.signInWithGoogle(`${location.origin}/`))
            .catch((err: Error) =>
              setState({ kind: 'error', message: err.message }),
            );
        });
        return;
      case 'probing':
        el.innerHTML = `<span class="account-hint">signing in…</span>`;
        return;
      case 'signed_in':
        el.innerHTML = `
          <span class="account-identity">
            <span class="account-name">${escapeHtml(accountLabel(state.player))}</span>
            ${showAdminLink(state.player) ? '<a class="account-admin-link" href="/admin/">Admin panel</a>' : ''}
          </span>
          <button id="account-sign-out" type="button">Sign out</button>`;
        el.querySelector('#account-sign-out')?.addEventListener('click', () => {
          void loadAuth()
            .then((a) => a.signOut())
            .then(() => setState({ kind: 'signed_out' }));
        });
        return;
      case 'unlinked':
        el.innerHTML = `
          <span class="account-hint account-warn">account not linked — use the mobile app's
          Settings → Link Google Account first</span>
          <button id="account-retry" type="button">OK</button>`;
        el.querySelector('#account-retry')?.addEventListener('click', () =>
          setState({ kind: 'signed_out' }),
        );
        return;
      case 'error':
        el.innerHTML = `
          <span class="account-hint account-warn">${escapeHtml(state.message)}</span>
          <button id="account-retry" type="button">OK</button>`;
        el.querySelector('#account-retry')?.addEventListener('click', () =>
          setState({ kind: 'signed_out' }),
        );
        return;
    }
  }

  async function probe(a: AuthModule): Promise<void> {
    const token = await a.getAccessToken();
    if (!token) {
      setState({ kind: 'signed_out' });
      return;
    }
    let outcome: AccountProbe;
    try {
      outcome = { kind: 'player', player: await api.me(token) };
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        outcome = { kind: 'not_found' };
      } else if (err instanceof ApiError && err.status === 401) {
        outcome = { kind: 'unauthorized' };
      } else {
        outcome = {
          kind: 'failed',
          message: err instanceof Error ? err.message : String(err),
        };
      }
    }
    const next = applyAccountProbe(outcome);
    if (shouldDropSession(next)) await a.signOut();
    setState(next);
  }

  async function boot(): Promise<void> {
    if (!shouldInitAuth(Object.keys(localStorage), location.search, location.hash)) {
      render(); // clean visit: signed-out UI, no supabase-js loaded
      return;
    }
    const a = await loadAuth();
    const { oauthErrorFromUrl } = await import('../auth/oauth-error');
    const oauthError = oauthErrorFromUrl(location.search, location.hash);
    if (oauthError) {
      history.replaceState(null, '', location.pathname);
      setState({ kind: 'error', message: oauthError });
      return;
    }
    setState({ kind: 'probing' });
    // The PKCE code exchange completes asynchronously after the redirect;
    // probe now and re-probe when the session lands.
    a.supabase().auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' && state.kind !== 'signed_in') {
        void probe(a);
      }
    });
    await probe(a);
  }

  void boot();
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
